import { useEffect, useState } from "react";
import { api, num, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Th = ({ children, right }) => <th className={`${right ? "text-right" : "text-left"} px-4 py-2.5`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) => (
  <td className={`px-4 py-2.5 ${right ? "text-right tabular-nums" : ""} ${mono ? "font-mono text-xs" : ""} ${className}`}>{children}</td>
);
const Empty = ({ cols, text }) => <tr><td colSpan={cols} className="text-center py-8 text-sm text-slate-500">{text}</td></tr>;

function Panel({ title, children, testid }) {
  return (
    <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</div>
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

const ProgressBar = ({ pct }) => (
  <div className="flex items-center gap-2 min-w-[110px]">
    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
    <span className="text-xs tabular-nums text-slate-600 w-10 text-right">{pct}%</span>
  </div>
);

const StatusBadge = ({ s }) => {
  const cls = s === "Fully Dispatched" || s === "Dispatched" || s === "Delivered" ? "bg-emerald-50 text-emerald-700" :
    s === "Partially Dispatched" ? "bg-amber-50 text-amber-700" :
    s === "Cancelled" || s === "Delayed" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-700";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider whitespace-nowrap ${cls}`}>{s}</span>;
};

export default function Consignments() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/consignments/reports").then((r) => setD(r.data)); }, []);

  return (
    <div className="space-y-4" data-testid="consignments-page">
      <PageHeader title="Consignments" subtitle="Consignment dispatch reports & PO balance tracking" />
      <Tabs defaultValue="balance">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="balance" data-testid="ctab-balance">Balance PO</TabsTrigger>
          <TabsTrigger value="pending" data-testid="ctab-pending">Pending Dispatches</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="ctab-schedule">Upcoming Schedule</TabsTrigger>
          <TabsTrigger value="history" data-testid="ctab-history">Dispatch History</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="ctab-invoices">Consignment Invoices</TabsTrigger>
          <TabsTrigger value="customers" data-testid="ctab-customers">Customer-wise Pending</TabsTrigger>
        </TabsList>

        <TabsContent value="balance">
          <Panel title="Balance PO Report — ordered vs dispatched per order" testid="rep-balance">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr><Th>Order</Th><Th>Customer</Th><Th right>PO Qty</Th><Th right>Invoiced</Th><Th right>Pending</Th><Th right>Consignments</Th><Th>Progress</Th><Th>Status</Th></tr>
            </thead>
            <tbody>
              {d?.balance_po?.map((r) => (
                <tr key={r.sales_order_id} className="border-t border-slate-100">
                  <Td mono>{r.order_number}{r.po_number ? <div className="text-[10px] text-slate-400 font-sans">Cust. PO: {r.po_number}</div> : null}</Td>
                  <Td>{r.customer}</Td>
                  <Td right>{num(r.total_qty)}</Td>
                  <Td right>{num(r.invoiced_qty)}</Td>
                  <Td right className={r.pending_qty > 0 ? "text-amber-700 font-medium" : "text-emerald-700"}>{num(r.pending_qty)}</Td>
                  <Td right>{r.consignments_count}</Td>
                  <Td><ProgressBar pct={r.progress_pct} /></Td>
                  <Td><StatusBadge s={r.status} /></Td>
                </tr>
              ))}
              {(!d || d.balance_po.length === 0) && <Empty cols={8} text="No consignment-based orders yet. Set an order's Invoicing to 'Consignment' under Sales Orders." />}
            </tbody>
          </Panel>
        </TabsContent>

        <TabsContent value="pending">
          <Panel title="Pending Dispatches — invoiced consignments awaiting LR/Docket" testid="rep-pending">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr><Th>Consignment</Th><Th>Order</Th><Th>Customer</Th><Th>Invoice</Th><Th right>Qty</Th><Th>Created</Th></tr>
            </thead>
            <tbody>
              {d?.pending_dispatches?.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <Td mono>{c.number}</Td>
                  <Td mono>{c.order_number}</Td>
                  <Td>{c.customer_name}</Td>
                  <Td mono>{c.invoice_number}</Td>
                  <Td right>{num(c.quantity)}</Td>
                  <Td className="text-xs text-slate-500">{fmtDate(c.created_at)}</Td>
                </tr>
              ))}
              {(!d || d.pending_dispatches.length === 0) && <Empty cols={6} text="Nothing pending — all consignments dispatched." />}
            </tbody>
          </Panel>
        </TabsContent>

        <TabsContent value="schedule">
          <Panel title="Upcoming Dispatch Schedule" testid="rep-schedule">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr><Th>Order</Th><Th>Planned Date</Th><Th right>Planned Qty</Th><Th>Actual Date</Th><Th right>Actual Qty</Th><Th>Status</Th></tr>
            </thead>
            <tbody>
              {d?.upcoming_schedule?.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <Td mono>{s.order_number}</Td>
                  <Td>{fmtDate(s.planned_date)}</Td>
                  <Td right>{num(s.planned_qty)}</Td>
                  <Td>{s.actual_date ? fmtDate(s.actual_date) : "-"}</Td>
                  <Td right>{s.actual_qty ? num(s.actual_qty) : "-"}</Td>
                  <Td><StatusBadge s={s.status} /></Td>
                </tr>
              ))}
              {(!d || d.upcoming_schedule.length === 0) && <Empty cols={6} text="No upcoming scheduled dispatches." />}
            </tbody>
          </Panel>
        </TabsContent>

        <TabsContent value="history">
          <Panel title="Dispatch History — consignments with LR/Docket" testid="rep-history">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr><Th>Consignment</Th><Th>Order</Th><Th>Customer</Th><Th>LR / Docket</Th><Th>Transporter</Th><Th right>Qty</Th><Th>Status</Th></tr>
            </thead>
            <tbody>
              {d?.dispatch_history?.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <Td mono>{c.number}</Td>
                  <Td mono>{c.order_number}</Td>
                  <Td>{c.customer_name}</Td>
                  <Td mono>{c.lr_number}</Td>
                  <Td>{c.transporter || "-"}</Td>
                  <Td right>{num(c.quantity)}</Td>
                  <Td><StatusBadge s={c.status} /></Td>
                </tr>
              ))}
              {(!d || d.dispatch_history.length === 0) && <Empty cols={7} text="No dispatched consignments yet." />}
            </tbody>
          </Panel>
        </TabsContent>

        <TabsContent value="invoices">
          <Panel title="Consignment-wise Invoice Report" testid="rep-invoices">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr><Th>Consignment</Th><Th>Invoice</Th><Th>Order</Th><Th>Customer</Th><Th right>Qty</Th><Th>Items</Th><Th>Date</Th></tr>
            </thead>
            <tbody>
              {d?.consignment_invoices?.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <Td mono>{c.number}</Td>
                  <Td mono>{c.invoice_number}</Td>
                  <Td mono>{c.order_number}</Td>
                  <Td>{c.customer_name}</Td>
                  <Td right>{num(c.quantity)}</Td>
                  <Td className="text-xs text-slate-600">{(c.items || []).map((i) => `${i.description} × ${num(i.quantity)}`).join("; ")}</Td>
                  <Td className="text-xs text-slate-500">{fmtDate(c.created_at)}</Td>
                </tr>
              ))}
              {(!d || d.consignment_invoices.length === 0) && <Empty cols={7} text="No consignment invoices yet." />}
            </tbody>
          </Panel>
        </TabsContent>

        <TabsContent value="customers">
          <Panel title="Customer-wise Pending Quantity" testid="rep-customers">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr><Th>Customer</Th><Th right>Pending Qty</Th><Th right>Open Orders</Th></tr>
            </thead>
            <tbody>
              {d?.customer_pending?.map((c) => (
                <tr key={c.customer} className="border-t border-slate-100">
                  <Td className="font-medium text-slate-800">{c.customer}</Td>
                  <Td right className="text-amber-700 font-medium">{num(c.pending_qty)}</Td>
                  <Td right>{c.orders}</Td>
                </tr>
              ))}
              {(!d || d.customer_pending.length === 0) && <Empty cols={3} text="No pending quantities." />}
            </tbody>
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
